import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../config/backend';
import { ref, get as rtdbGet, set, child } from '../services/phpDataAdapter';
import {
  calculateNutrition,
  PatientData,
  NutritionCalculation as NutritionCalculationType,
  CalculationProtocol,
  getProtocolName,
  PersonType,
  detectPersonType,
  getPersonTypeName,
  getProtocolDescription,
  getRecommendedProtocol,
  mapPatientGoalToCalculationGoal,
} from '../services/nutritionCalculationService';
import './Pages.css';

export const NutritionCalculation: React.FC = () => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const [selectedPatientId, setSelectedPatientId] = useState<string>(patientId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patientData, setPatientData] = useState<PatientData>({
    weight: 70,
    height: 170,
    age: 30,
    gender: 'male',
    activityLevel: 'moderate',
    goal: 'maintenance',
  });
  const [calculation, setCalculation] = useState<NutritionCalculationType | null>(null);
  const [savedCalculation, setSavedCalculation] = useState<NutritionCalculationType | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState<CalculationProtocol>('mifflin_st_jeor');
  const [selectedPersonType, setSelectedPersonType] = useState<PersonType | undefined>(undefined);
  const goalConfirmed = useRef(true);
  const patientRequest = useRef(0);
  const [goalNeedsReview, setGoalNeedsReview] = useState(false);
  const [patientError, setPatientError] = useState<string | null>(null);
  const [registeredGoal, setRegisteredGoal] = useState('');

  useEffect(() => {
    if (patientId) {
      setSelectedPatientId(patientId);
    }
  }, [patientId]);

  useEffect(() => {
    if (selectedPatientId) {
      loadPatientData();
    } else {
      patientRequest.current++;
      goalConfirmed.current = false;
      setGoalNeedsReview(true);
      setCalculation(null);
      setSavedCalculation(null);
    }
    return () => { patientRequest.current++; };
  }, [selectedPatientId]);

  const calculateAge = (birthDate: string): number => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const loadPatientData = async () => {
    if (!selectedPatientId) return;
    const request = ++patientRequest.current;
    goalConfirmed.current = false;
    setGoalNeedsReview(true);
    setCalculation(null);
    setSavedCalculation(null);
    setPatientError(null);
    setLoading(true);
    try {
      const [userSnap, patientSnap] = await Promise.all([
        rtdbGet(child(ref(database), `users/${selectedPatientId}`)),
        rtdbGet(child(ref(database), `patients/${selectedPatientId}`)),
      ]);

      if (request !== patientRequest.current) return;
      const userData = userSnap.exists() ? userSnap.val() : {};
      const patData = patientSnap.exists() ? patientSnap.val() : {};
      const rawGoal = userData.goals ?? userData.goal ?? patData.goal ?? patData.goals;
      const rawActivity = String(userData.activityLevel ?? patData.activityLevel ?? 'moderate')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      const activityLevel: PatientData['activityLevel'] =
        rawActivity.includes('muito ativo') || rawActivity.includes('very_active') ? 'very_active'
          : rawActivity.includes('moderadamente') || rawActivity.includes('moderate') ? 'moderate'
            : rawActivity.includes('sedent') ? 'sedentary'
              : rawActivity.includes('leve') || rawActivity.includes('light') ? 'light'
                : rawActivity.includes('ativo') || rawActivity.includes('active') ? 'active'
                  : 'moderate';
      const rawGender = String(userData.personalData?.gender ?? userData.gender ?? patData.gender ?? 'male').toLowerCase();
      const gender: PatientData['gender'] = rawGender === 'female' || rawGender === 'feminino' || rawGender === 'f' ? 'female' : 'male';
      const rawRegisteredGoal = Array.isArray(rawGoal) ? rawGoal.join(', ') : String(rawGoal ?? '');
      setRegisteredGoal(rawRegisteredGoal);
      const mappedGoal = mapPatientGoalToCalculationGoal(rawGoal);
      const data: Partial<PatientData> = {
        weight: Number(userData.weight ?? patData.weight) || 70,
        height: Number(userData.height ?? patData.height) || 170,
        age: userData.personalData?.birthDate
          ? calculateAge(userData.personalData.birthDate)
          : Number(userData.age ?? patData.age) || 30,
        gender,
        activityLevel,
        goal: mappedGoal,
      };

      // Carregar cálculo salvo
      const calcSnap = await rtdbGet(child(ref(database), `users/${selectedPatientId}/nutritionCalculation`));
      if (request !== patientRequest.current) return;
      goalConfirmed.current = mappedGoal !== undefined;
      setGoalNeedsReview(!goalConfirmed.current);
      if (calcSnap.exists()) {
        const saved = calcSnap.val() as NutritionCalculationType;
        setSavedCalculation(mappedGoal ? saved : null);
        if (saved.protocol) {
          setSelectedProtocol(saved.protocol);
        }
      }

      const finalData = {
        weight: data.weight || 70,
        height: data.height || 170,
        age: data.age || 30,
        gender: data.gender || 'male',
        activityLevel: data.activityLevel || 'moderate',
        goal: data.goal || 'maintenance',
        personType: data.personType,
        pregnancyTrimester: data.pregnancyTrimester,
      } as PatientData;

      // Detecta tipo de pessoa se não foi fornecido
      if (!finalData.personType) {
        setSelectedPersonType(undefined); // Auto-detect
      } else {
        setSelectedPersonType(finalData.personType);
      }

      setPatientData(finalData);
      performCalculation(finalData);
    } catch (error) {
      if (request !== patientRequest.current) return;
      setPatientError('Não foi possível carregar o paciente. Selecione-o novamente antes de calcular.');
      console.error('Erro ao carregar dados:', error);
    } finally {
      if (request === patientRequest.current) setLoading(false);
    }
  };

  const performCalculation = (data: PatientData, protocol: CalculationProtocol = selectedProtocol) => {
    if (!goalConfirmed.current) { setCalculation(null); return; }
    // Se personType não foi definido manualmente, detecta automaticamente
    const personTypeToUse = selectedPersonType || detectPersonType(
      data.age,
      data.gender,
      data.activityLevel,
      data.pregnancyTrimester
    );

    const dataWithPersonType = {
      ...data,
      personType: personTypeToUse,
    };

    const result = calculateNutrition(dataWithPersonType, protocol);
    setCalculation(result);
  };

  const handleFieldChange = (field: keyof PatientData, value: any) => {
    if (field === 'goal') {
      goalConfirmed.current = !!value;
      setGoalNeedsReview(!value);
    }
    const updated = { ...patientData, [field]: value };
    setPatientData(updated);

    // Se idade, gênero ou atividade mudaram, atualiza detecção automática
    if (field === 'age' || field === 'gender' || field === 'activityLevel' || field === 'pregnancyTrimester') {
      const detectedType = detectPersonType(
        field === 'age' ? value : updated.age,
        field === 'gender' ? value : updated.gender,
        field === 'activityLevel' ? value : updated.activityLevel,
        field === 'pregnancyTrimester' ? value : updated.pregnancyTrimester
      );
      setSelectedPersonType(detectedType);
    }

    performCalculation(updated, selectedProtocol);
  };

  const handlePersonTypeChange = (personType: PersonType | 'auto') => {
    if (personType === 'auto') {
      const detected = detectPersonType(
        patientData.age,
        patientData.gender,
        patientData.activityLevel,
        patientData.pregnancyTrimester
      );
      setSelectedPersonType(undefined); // undefined = auto-detect
      const updated = { ...patientData, personType: undefined };
      setPatientData(updated);
      performCalculation(updated, selectedProtocol);
    } else {
      setSelectedPersonType(personType);
      const updated = { ...patientData, personType };
      setPatientData(updated);
      performCalculation(updated, selectedProtocol);
    }
  };

  const handleProtocolChange = (protocol: CalculationProtocol) => {
    setSelectedProtocol(protocol);
    performCalculation(patientData, protocol);
  };

  const handleSave = async () => {
    if (!selectedPatientId || !calculation || !goalConfirmed.current || loading || patientError) return;
    setSaving(true);
    try {
      await set(ref(database, `users/${selectedPatientId}/nutritionCalculation`), {
        ...calculation,
        protocol: selectedProtocol,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setSavedCalculation(calculation);
      alert('Cálculo salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar cálculo:', error);
      alert('Erro ao salvar cálculo');
    } finally {
      setSaving(false);
    }
  };

  const getGoalLabel = (goal: string): string => {
    const labels: Record<string, string> = {
      weight_loss: 'Emagrecimento',
      maintenance: 'Manutenção',
      muscle_gain: 'Ganho de Massa Muscular',
      health: 'Saúde Geral',
      performance: 'Performance',
      body_composition: 'Composição Corporal',
    };
    return labels[goal] || goal;
  };

  const getActivityLabel = (level: string): string => {
    const labels: Record<string, string> = {
      sedentary: 'Sedentário',
      light: 'Leve',
      moderate: 'Moderado',
      active: 'Ativo',
      very_active: 'Muito Ativo',
    };
    return labels[level] || level;
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Cálculo Nutricional</h2>
          <p>Calcule as necessidades nutricionais do paciente (TMB, GET, macronutrientes).</p>
        </div>
      </div>

      <div className="ns-grid ns-grid-2">
        <Card title="Dados do Paciente" subtitle="Preencha ou ajuste os dados">
          <div className="ns-form">
            <label className="ns-field">
              <span>Paciente *</span>
              <select
                className="ns-input"
                value={selectedPatientId}
                onChange={(e) => setSelectedPatientId(e.target.value)}
              >
                <option value="">Selecione um paciente</option>
                {pacientes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>

            <div className="ns-form-row">
              <label className="ns-field">
                <span>Peso (kg) *</span>
                <input
                  type="number"
                  className="ns-input"
                  value={patientData.weight}
                  onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                />
              </label>

              <label className="ns-field">
                <span>Altura (cm) *</span>
                <input
                  type="number"
                  className="ns-input"
                  value={patientData.height}
                  onChange={(e) => handleFieldChange('height', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
              </label>
            </div>

            <div className="ns-form-row">
              <label className="ns-field">
                <span>Idade (anos) *</span>
                <input
                  type="number"
                  className="ns-input"
                  value={patientData.age}
                  onChange={(e) => handleFieldChange('age', parseInt(e.target.value) || 0)}
                  min="0"
                  max="120"
                />
              </label>

              <label className="ns-field">
                <span>Gênero *</span>
                <select
                  className="ns-input"
                  value={patientData.gender}
                  onChange={(e) => handleFieldChange('gender', e.target.value as 'male' | 'female')}
                >
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                </select>
              </label>
            </div>

            <label className="ns-field">
              <span>Nível de Atividade *</span>
              <select
                className="ns-input"
                value={patientData.activityLevel}
                onChange={(e) => handleFieldChange('activityLevel', e.target.value as PatientData['activityLevel'])}
              >
                <option value="sedentary">Sedentário</option>
                <option value="light">Leve (1-3 dias/semana)</option>
                <option value="moderate">Moderado (3-5 dias/semana)</option>
                <option value="active">Ativo (6-7 dias/semana)</option>
                <option value="very_active">Muito Ativo (2x ao dia)</option>
              </select>
            </label>

            <label className="ns-field">
              <span>Objetivo *</span>
              <select
                className="ns-input"
                value={goalNeedsReview ? '' : patientData.goal}
                disabled={loading || !!patientError}
                onChange={(e) => handleFieldChange('goal', e.target.value as PatientData['goal'])}
              >
                <option value="" disabled>Selecione o objetivo para confirmar</option>
                <option value="weight_loss">Emagrecimento</option>
                <option value="maintenance">Manutenção</option>
                <option value="muscle_gain">Ganho de Massa Muscular</option>
                <option value="health">Saúde Geral</option>
                <option value="performance">Performance</option>
                <option value="body_composition">Composição Corporal</option>
              </select>
              {patientError && <small role="alert">{patientError}</small>}
              {goalNeedsReview && <small role="alert">Objetivo ausente ou não reconhecido. Escolha uma categoria antes de calcular.</small>}
              {registeredGoal && (
                <small className="ns-field-hint" role="status">
                  Objetivo do cadastro: {registeredGoal}. Categoria aplicada ao cálculo: {goalNeedsReview ? 'Aguardando revisão' : getGoalLabel(patientData.goal)}.
                </small>
              )}
            </label>

            <label className="ns-field">
              <span>Tipo de Pessoa *</span>
              <select
                className="ns-input"
                value={selectedPersonType || 'auto'}
                onChange={(e) => handlePersonTypeChange(e.target.value as PersonType | 'auto')}
              >
                <option value="auto">Detecção Automática</option>
                <option value="adult">Adulto</option>
                <option value="child">Criança/Adolescente</option>
                <option value="elderly">Idoso</option>
                <option value="pregnant">Gestante</option>
                <option value="athlete">Atleta</option>
              </select>
              {selectedPersonType && (
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--page-text-muted)', fontSize: '11px' }}>
                  Tipo selecionado: {getPersonTypeName(selectedPersonType)}
                </small>
              )}
              {!selectedPersonType && (
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--page-text-muted)', fontSize: '11px' }}>
                  {(() => {
                    const detected = detectPersonType(
                      patientData.age,
                      patientData.gender,
                      patientData.activityLevel,
                      patientData.pregnancyTrimester
                    );
                    return `Tipo detectado: ${getPersonTypeName(detected)}`;
                  })()}
                </small>
              )}
            </label>

            {selectedPersonType === 'pregnant' && (
              <label className="ns-field">
                <span>Trimestre de Gestação *</span>
                <select
                  className="ns-input"
                  value={patientData.pregnancyTrimester || 1}
                  onChange={(e) => handleFieldChange('pregnancyTrimester', parseInt(e.target.value) as 1 | 2 | 3)}
                >
                  <option value="1">1º Trimestre</option>
                  <option value="2">2º Trimestre (+340 kcal)</option>
                  <option value="3">3º Trimestre (+452 kcal)</option>
                </select>
              </label>
            )}

            {/* Recomendação do Sistema */}
            {(() => {
              const recommendation = getRecommendedProtocol(patientData);
              const isRecommendedSelected = selectedProtocol === recommendation.protocol;

              return (
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #10b981' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span style={{ fontSize: '20px' }}>💡</span>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#047857', fontWeight: 600 }}>
                        Recomendação Clínica: {getProtocolName(recommendation.protocol)}
                      </h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#065f46', lineHeight: 1.5 }}>
                        {recommendation.reason}
                      </p>
                      {!isRecommendedSelected && (
                        <button
                          onClick={() => handleProtocolChange(recommendation.protocol)}
                          style={{ marginTop: '8px', background: '#059669', color: 'white', border: 'none', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                        >
                          Aplicar Recomendação
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <label className="ns-field">
              <span>Protocolo de Cálculo *</span>
              <select
                className="ns-input"
                value={selectedProtocol}
                onChange={(e) => handleProtocolChange(e.target.value as CalculationProtocol)}
              >
                <option value="mifflin_st_jeor">Mifflin-St Jeor (Recomendado)</option>
                <option value="harris_benedict">Harris-Benedict (Revisado)</option>
                <option value="katch_mcardle">Katch-McArdle</option>
                <option value="fao_who">FAO/WHO</option>
              </select>
            </label>

            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #3b82f6', fontSize: '13px', color: 'var(--page-text-muted)', lineHeight: '1.5' }}>
              <strong>Como funciona:</strong> {getProtocolDescription(selectedProtocol)}
            </div>

          </div>
        </Card>

        <Card
          title="Resultados do Cálculo"
          subtitle={calculation ? `${calculation.protocolName || getProtocolName(selectedProtocol)}${calculation.personTypeName ? ` - ${calculation.personTypeName}` : ''} - Objetivo: ${getGoalLabel(patientData.goal)}` : 'Preencha os dados do paciente'}
        >
          {loading ? (
            <p>Carregando dados do paciente...</p>
          ) : calculation ? (
            <div className="ns-calculation-results">
              {/* Informações sobre Fórmula e Tipo de Pessoa */}
              {calculation.personTypeName && (
                <div className="ns-result-card" style={{ background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)', borderColor: '#0ea5e9', marginBottom: '16px' }}>
                  <div className="ns-result-icon">ℹ️</div>
                  <div className="ns-result-content">
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#0c4a6e' }}>
                      Fórmula Aplicada: {calculation.personTypeName}
                    </h3>
                    <p style={{ margin: 0, fontSize: '12px', color: '#075985', lineHeight: 1.5 }}>
                      {(() => {
                        const type = calculation.personType;
                        if (type === 'child') {
                          return 'Usando fórmula de Schofield (1985) específica para crianças/adolescentes, ajustada por faixa etária e gênero.';
                        } else if (type === 'elderly') {
                          return 'Usando fórmula ajustada para idosos com redução de 5-10% devido à perda de massa muscular relacionada à idade.';
                        } else if (type === 'pregnant') {
                          const trimester = patientData.pregnancyTrimester || 1;
                          const adjustments = { 1: '0', 2: '+340', 3: '+452' };
                          return `Usando fórmula para gestantes com ajuste do ${trimester}º trimestre (${adjustments[trimester as keyof typeof adjustments]} kcal) conforme IOM.`;
                        } else if (type === 'athlete') {
                          return 'Usando fórmula padrão com fatores de atividade elevados para atletas.';
                        } else {
                          return 'Usando fórmula padrão para adultos.';
                        }
                      })()}
                    </p>
                  </div>
                </div>
              )}

              <div className="ns-result-card ns-result-bmr">
                <div className="ns-result-icon">🔥</div>
                <div className="ns-result-content">
                  <h3>TMB (Taxa Metabólica Basal)</h3>
                  <p className="ns-result-value">{calculation.bmr} kcal</p>
                  <p className="ns-result-description">
                    Calorias que o corpo queima em repouso
                  </p>
                </div>
              </div>

              <div className="ns-result-card ns-result-tdee">
                <div className="ns-result-icon">⚡</div>
                <div className="ns-result-content">
                  <h3>GET (Gasto Energético Total)</h3>
                  <p className="ns-result-value">{calculation.tdee} kcal</p>
                  <p className="ns-result-description">
                    Calorias totais gastas por dia (com atividade física)
                  </p>
                </div>
              </div>

              <div className="ns-result-card ns-result-target">
                <div className="ns-result-icon">🎯</div>
                <div className="ns-result-content">
                  <h3>Calorias Alvo</h3>
                  <p className="ns-result-value">{calculation.targetCalories} kcal</p>
                  <p className="ns-result-description">
                    {patientData.goal === 'weight_loss'
                      ? 'Déficit calórico para emagrecimento'
                      : patientData.goal === 'muscle_gain'
                        ? 'Superávit calórico para ganho de massa'
                        : patientData.goal === 'body_composition'
                          ? 'Ajuste energético para composição corporal'
                          : patientData.goal === 'performance'
                            ? 'Ajuste energético para performance'
                            : patientData.goal === 'health'
                              ? 'Necessidades energéticas para saúde geral'
                              : 'Manutenção do peso atual'}
                  </p>
                </div>
              </div>

              <div className="ns-result-card ns-result-macros">
                <div className="ns-result-icon">📊</div>
                <div className="ns-result-content">
                  <h3>Distribuição de Macronutrientes</h3>
                  <div className="ns-macros-grid">
                    <div className="ns-macro-item">
                      <span className="ns-macro-label">Proteínas</span>
                      <span className="ns-macro-value">{calculation.macros.protein} g</span>
                      <span style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>{calculation.macrosPercent.protein}%</span>
                    </div>
                    <div className="ns-macro-item">
                      <span className="ns-macro-label">Carboidratos</span>
                      <span className="ns-macro-value">{calculation.macros.carbohydrates} g</span>
                      <span style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>{calculation.macrosPercent.carbohydrates}%</span>
                    </div>
                    <div className="ns-macro-item">
                      <span className="ns-macro-label">Gorduras</span>
                      <span className="ns-macro-value">{calculation.macros.fats} g</span>
                      <span style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>{calculation.macrosPercent.fats}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ns-result-card ns-result-water">
                <div className="ns-result-icon">💧</div>
                <div className="ns-result-content">
                  <h3>Ingestão de Água Recomendada</h3>
                  <p className="ns-result-value">{(calculation.waterIntake / 1000).toFixed(1)} L/dia</p>
                  <p className="ns-result-description" style={{ marginTop: '4px', fontSize: '11px' }}>
                    ({calculation.waterIntake} ml)
                  </p>
                  <p className="ns-result-description">
                    Baseado no peso e nível de atividade
                  </p>
                </div>
              </div>

              {/* Comparação de Protocolos */}
              {calculation.bmrByProtocol && (
                <div className="ns-result-card" style={{ background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', borderColor: '#d1d5db' }}>
                  <div className="ns-result-icon">📊</div>
                  <div className="ns-result-content">
                    <h3>Comparação de Protocolos (TMB)</h3>
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {Object.entries(calculation.bmrByProtocol).map(([protocol, bmr]) => (
                        <div key={protocol} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: 'rgba(255, 255, 255, 0.6)', borderRadius: '6px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--page-text-muted)' }}>{getProtocolName(protocol as CalculationProtocol)}:</span>
                          <strong style={{ fontSize: '14px', color: protocol === calculation.protocol ? '#0ea5e9' : '#64748b' }}>
                            {Math.round(bmr)} kcal
                            {protocol === calculation.protocol && ' (Atual)'}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="ns-form-actions" style={{ marginTop: '24px' }}>
                <Button
                  onClick={handleSave}
                  disabled={saving || !selectedPatientId}
                >
                  {saving ? 'Salvando...' : 'Salvar Cálculo'}
                </Button>
              </div>

              {savedCalculation && (savedCalculation as any).updatedAt && (
                <div className="ns-saved-indicator">
                  <p>✓ Cálculo salvo anteriormente em {new Date((savedCalculation as any).updatedAt).toLocaleDateString('pt-BR')}</p>
                </div>
              )}
            </div>
          ) : (
            <p>Selecione um paciente e preencha os dados para calcular.</p>
          )}
        </Card>
      </div>
    </div>
  );
};
